import { useSearchParams } from 'react-router-dom'
import { ClubMemberView } from '@/types'
import { ROUTE_PLAYER_PROFILE } from '@/constants/Routes'
import { OperationEntitlements } from '../hooks/useClubEntitlements'
import { EntitlementIcons } from './ui'

interface MemberRowProps {
  member: ClubMemberView
  entitlements?: OperationEntitlements
  isEditMode?: boolean
  isSelected?: boolean
  onToggleSelect?: (userId: number) => void
  /** When true, the row advertises the coaching shortcut (gated upstream). */
  coachingEnabled?: boolean
  /** Set only for pending join requests the viewer may approve: the row then offers Accept. */
  onApprove?: (userId: number) => void
}

export const MemberRow = ({
  member,
  entitlements,
  isEditMode = false,
  isSelected = false,
  onToggleSelect,
  coachingEnabled = false,
  onApprove,
}: MemberRowProps) => {
  const [, setSearchParams] = useSearchParams()
  const isUnclaimed = member.state === 'UNCLAIMED_INVITE'

  // Open the in-club member view by writing the `member` query param — keeps the manager in context
  // (no navigate-away) and makes the open member shareable/deep-linkable. `memberTab` selects which
  // tab opens: omitted/overview for a row click, 'coaching' for the coaching shortcut.
  const openMember = (tab?: 'coaching') => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('member', String(member.userId))
      if (tab) next.set('memberTab', tab)
      else next.delete('memberTab')
      return next
    })
  }

  const handleClick = () => {
    if (isEditMode && onToggleSelect) {
      onToggleSelect(member.userId)
    } else {
      openMember()
    }
  }

  return (
    <tr
      className={`border-b border-gray-100 last:border-b-0 transition-colors cursor-pointer ${
        isEditMode && isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
      }`}
      onClick={handleClick}
    >
      {/* Checkbox */}
      {isEditMode && (
        <td className="py-2 px-2 w-8">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect?.(member.userId)}
            onClick={(e) => e.stopPropagation()}
            className="w-3.5 h-3.5 accent-primary-600"
          />
        </td>
      )}

      {/* Name + DUPR ID + entitlement badges */}
      <td className="py-2 px-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-gray-200 flex items-center justify-center flex-shrink-0 text-[10px] font-medium text-gray-500">
            {member.fullName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-gray-900 truncate">{member.fullName}</span>
              {isUnclaimed && (
                <span className="px-1 py-0.5 text-[9px] font-semibold bg-orange-100 text-orange-700">Unclaimed</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-400">{member.duprId}</span>
              {entitlements && <EntitlementIcons entitlements={entitlements} />}
            </div>
          </div>
        </div>
      </td>

      {/* Singles Rating */}
      <td className="py-2 px-3 text-center">
        <span className="text-sm text-gray-900">{member.singles === 'NR' ? '-' : member.singles}</span>
      </td>

      {/* Doubles Rating */}
      <td className="py-2 px-3 text-center">
        <span className="text-sm text-gray-900">{member.doubles === 'NR' ? '-' : member.doubles}</span>
      </td>

      {/* Joined */}
      <td className="py-2 px-3 text-center hidden md:table-cell">
        <span className="text-xs text-gray-500">{new Date(member.joinedAt).toLocaleDateString()}</span>
      </td>

      {/* Row actions */}
      <td className="py-2 px-3 text-right whitespace-nowrap">
        {!isEditMode && (
          <span className="inline-flex items-center gap-3">
            {onApprove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onApprove(member.userId)
                }}
                className="px-2 py-0.5 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700"
              >
                Accept
              </button>
            )}
            {coachingEnabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  openMember('coaching')
                }}
                className="text-xs font-medium text-primary-600 hover:text-primary-500"
              >
                Coaching
              </button>
            )}
            <a
              href={`${ROUTE_PLAYER_PROFILE}/${member.userId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="View public profile (new tab)"
              className="inline-flex items-center gap-0.5 text-xs text-gray-400 hover:text-gray-600"
            >
              Profile
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5h5m0 0v5m0-5L10 14M19 13v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h5"
                />
              </svg>
            </a>
          </span>
        )}
      </td>
    </tr>
  )
}
